//react
import { useEffect, useState, useMemo, useRef } from "react";
// router
import {
  useMatch,
  useNavigate,
  useParams,
  Link as RouterLink,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider, Field } from "formik";
import Alert from "@mui/material/Alert";

//mui components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormLabel from "@mui/material/FormLabel";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { LoadingButton } from "@mui/lab";

//custom constants, hooks and components
import {
  getPriceGroupDefaultValue,
  initialState,
} from "./PriceGroup.constants";
import { priceGroupValidationSchema } from "src/util/validationSchemas";
import enums, { FEATURE } from "src/util/enums";
import { Loader } from "../Loader";
import PriceGroupCopyModal from "./PriceGroupCopyModal";
import { is } from "src/util/is";
import { calculateTotalHours } from "src/utils/utility";
import {
  getTeamUrl,
  priceGroupIdUrl,
  priceGroupUrl,
} from "src/react-query/endPoints";
import { v4 as uuidv4 } from "uuid";

//icons
import { Icon } from "@iconify/react";
import AlertCircleFill from "@iconify/icons-eva/alert-circle-fill";
//hooks
import { useAccess } from "src/hooks/useAccess.hook";
import {
  useGetDetail,
  useGetPaymentDetailsForPriceGroup,
  useMutate,
} from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";

//redux actions
import { alertSlice } from "src/store/alert/alert.slice";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { giveTimeAndUTCTimeOffset } from "src/utils/formatTime";
import { fCurrencyWithDomain } from "src/utils/formatNumber";

export const PriceGroupForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedRef = useRef(null);
  const [priceGroupState, setPriceGroupState] = useState(initialState);
  const [submissionError, setSubmissionError] = useState(null);
  const { teamId } = useSelectedTeam();
  const isCreatePriceGroup = useMatch("/pricegroup/new");
  const { priceGroupId } = useParams();
  const hasReadOnlyAccess = useAccess(FEATURE.PRICEGROUP, "R");
  const { data: dialogData } = useSelector(({ dialog }) => dialog);

  const isReadOnly = hasReadOnlyAccess && !isCreatePriceGroup;
  const { data, isFetching } = useGetDetail("priceGroup", {
    id: priceGroupId,
  });
  const { mutate, isLoading } = useMutate("priceGroups");

  const { data: paymentDetails, isFetching: isPaymentDetailsFetching } =
    useGetPaymentDetailsForPriceGroup(isCreatePriceGroup, teamId);

  const onSubmit = async (priceGroupData, { setSubmitting }) => {
    const {
      actions: { setAlert },
    } = alertSlice;
    const { priceGroupType } = priceGroupData;
    const submitData = { ...priceGroupData };

    submitData.priceDetails = priceGroupData.priceDetails.map((detail) => {
      const processedDetail = { ...detail };

      if (detail.priceType === enums.PRICE_PER.MIN && detail.priceperHr) {
        processedDetail.priceperMin = parseFloat(detail.priceperHr) / 60;
      } else {
        processedDetail.priceperMin = "";
      }

      return processedDetail;
    });

    const mutateObject = {
      url: isCreatePriceGroup
        ? getTeamUrl(teamId, priceGroupUrl)
        : getTeamUrl(teamId, priceGroupIdUrl(priceGroupId)),
      method: isCreatePriceGroup ? "POST" : "PUT",
    };

    let totalHours = calculateTotalHours(priceGroupData.priceDetails, -1);
    totalHours = Number(totalHours.toFixed(2));

    if (
      totalHours < 168 &&
      totalHours !== 167.86 &&
      priceGroupType === enums.PRICE_TYPE.VARIABLE
    ) {
      dispatch(
        setAlert({
          open: true,
          message: `Please cover all time slots upto 24 hours`,
          severity: enums.SEVERITY.ERROR,
        })
      );
      setSubmitting(false);
      return;
    }

    // Create success and error handlers that properly manage setSubmitting
    const onSuccessHandler = () => {
      setSubmitting(false);
      setSubmissionError(null); // Clear any previous errors
      navigate("/pricegroup");
    };

    const onErrorHandler = (error) => {
      setSubmitting(false);
      
      // Extract detailed error message
      let errorMessage = "An error occurred while saving the price group";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Handle validation errors from server
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        if (Array.isArray(validationErrors)) {
          errorMessage = validationErrors.join(", ");
        } else if (typeof validationErrors === 'object') {
          errorMessage = Object.entries(validationErrors)
            .map(([field, fieldError]) => `${field}: ${fieldError}`)
            .join(", ");
        }
      }
      
      // Set submission error for form display
      setSubmissionError(errorMessage);
      
      dispatch(
        setAlert({
          open: true,
          message: errorMessage,
          severity: enums.SEVERITY.ERROR,
        })
      );
    };

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: submitData,
      },
      { 
        onSuccess: onSuccessHandler,
        onError: onErrorHandler
      }
    );
  };


  const defaultValues = useMemo(
    () => getPriceGroupDefaultValue(teamId),
    [teamId]
  );

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: priceGroupValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  // Function to check if there are form validation errors
  const shouldShowFormErrors = () => {
    const hasErrors = Object.keys(errors).length > 0;
    const hasTouched = Object.values(touched).some(touched => touched);
    return hasErrors && hasTouched;
  };

  // Function to render error messages safely
  const renderErrorMessages = (errors) => {
    const errorList = [];
    
    const processError = (field, error, parentField = '') => {
      const fullFieldName = parentField ? `${parentField}.${field}` : field;
      
      if (Array.isArray(error)) {
        // Handle array errors (like priceDetails array)
        error.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            Object.entries(item).forEach(([subField, subError]) => {
              if (subError && typeof subError === 'string') {
                errorList.push(
                  <li key={`${fullFieldName}[${index}].${subField}`}>
                    <Typography variant="body2">
                      <strong>{fullFieldName}[{index}]</strong> - {subField}: {subError}
                    </Typography>
                  </li>
                );
              }
            });
          } else if (item && typeof item === 'string') {
            errorList.push(
              <li key={`${fullFieldName}[${index}]`}>
                <Typography variant="body2">
                  <strong>{fullFieldName}[{index}]:</strong> {item}
                </Typography>
              </li>
            );
          }
        });
      } else if (typeof error === 'object' && error !== null) {
        // Handle nested object errors
        Object.entries(error).forEach(([subField, subError]) => {
          processError(subField, subError, fullFieldName);
        });
      } else if (error && typeof error === 'string') {
        // Handle simple field errors
        errorList.push(
          <li key={fullFieldName}>
            <Typography variant="body2">
              <strong>{fullFieldName}:</strong> {error}
            </Typography>
          </li>
        );
      }
    };
    
    Object.entries(errors).forEach(([field, error]) => {
      processError(field, error);
    });
    
    return errorList;
  };

  const handleEvent = (e) => {
    const { day } = e.currentTarget.dataset;
    const toggle = !priceGroupState[day];
    selectedRef.current = toggle ? enums.DAYS[day] : null;

    setPriceGroupState({
      ...initialState,
      [day]: toggle,
    });
  };

  const onCheckedChanges = (e) => {
    const { name, checked } = e.target;
    const { DAYS_IN_STRING } = enums;
    const { copy } = initialState;
    const { copy: _copy } = priceGroupState;
    let copyDays = _copy;
    let isAll = false;

    if (name === "isAllSelected") {
      copyDays = checked ? DAYS_IN_STRING.map(() => !0) : copy;
      isAll = checked;
    }

    if (name.includes("day_")) {
      const _day = name.split("_")[1];
      const idx = DAYS_IN_STRING.findIndex((v) => v === _day);
      copyDays[idx] = checked;
      isAll = false;
    }

    setPriceGroupState((state) => ({
      ...state,
      copy: copyDays,
      isAll,
    }));
  };

  const onAddTimeRange =
    (idx = null, action = "A") =>
    () => {
      const { priceDetails } = values;
      const {
        actions: { setAlert },
      } = alertSlice;
      const dayValues = Object.values(priceGroupState);
      const hasSelectedDays = dayValues.filter((d) => d);

      if (is.null(idx) && action === "A") {
        const totalHours = Math.ceil(
          calculateTotalHours(priceDetails, selectedRef.current)
        );

        if (!hasSelectedDays.length) {
          dispatch(
            setAlert({
              open: true,
              message: "Please select the day before adding new time range",
              severity: enums.SEVERITY.ERROR,
            })
          );
          return;
        }

        if (totalHours >= 24) {
          dispatch(
            setAlert({
              open: true,
              message: `Already defined 24 hours for the selected day. Please adjust timeslot to define multiple prices`,
              severity: enums.SEVERITY.ERROR,
            })
          );
          return;
        }

        const { current } = selectedRef;
        const priceDetailCopy = { ...defaultValues.priceDetails[0] };
        const lastPriceDetail = priceDetails.findLast((p) => p.day === current);
        priceDetailCopy.startTime = lastPriceDetail.endTime;
        priceDetailCopy.day = selectedRef.current;
        setFieldValue("priceDetails", priceDetails.concat([priceDetailCopy]));
      }

      if (!is.null(idx) && action === "R") {
        setFieldValue(
          "priceDetails",
          priceDetails.filter((p, i) => i !== idx)
        );
      }

      if (!is.null(idx) && action === "S") {
        let priceDetailCopy = priceDetails[idx];
        priceDetailCopy = {
          ...defaultValues.priceDetails[0],
          day: priceDetailCopy.day,
        };
        priceDetails.splice(idx, 1, priceDetailCopy);
        setFieldValue("priceDetails", [...priceDetails]);
      }
    };

  const onHandleCopy =
    (day = null) =>
    () => {
      const { priceDetails } = values;
      const {
        actions: { setDialog },
      } = dialogSlice;
      const { copy, isAll } = priceGroupState;
      const { DAYS } = enums;
      let daysToCopy = [];
      let currentCopyDay = [];

      if (day) {
        dispatch(
          setDialog({
            open: true,
            id: { day },
          })
        );
      }

      if (!day) {
        currentCopyDay = priceDetails.filter(
          (c) => DAYS[dialogData.day] === c.day
        );
        copy.forEach((d, i) => {
          d &&
            (daysToCopy = daysToCopy.concat(
              currentCopyDay.map((c) => ({ ...c, day: i }))
            ));
          !d &&
            i !== DAYS[dialogData.day] &&
            (daysToCopy = daysToCopy.concat(
              priceDetails.filter((c) => c.day === i)
            ));
        });
        daysToCopy = isAll ? daysToCopy : daysToCopy.concat(currentCopyDay);
        dispatch(dialogSlice.actions.setDialog(false));
        setFieldValue("priceDetails", daysToCopy);
      }
    };

  const onHandleBlur = ({ name }) => {
    const matches = [...name.matchAll(/\[(\d+)]\.(\w+)/g)];
    const { priceDetails } = values;
    const filteredDays = priceDetails.filter(
      (f) => f.day !== selectedRef.current
    );
    const ranges = priceRangeInGroup[selectedRef.current];
    let [, index, fieldName] = matches[0];
    let rangeIndex = ranges.findIndex((r) => r.index === Number(index));

    if (fieldName === "startTime") {
      while (rangeIndex--) {
        ranges[rangeIndex].endTime = ranges[rangeIndex + 1].startTime;
      }
    }

    if (fieldName === "endTime") {
      while (rangeIndex++) {
        if (rangeIndex > ranges.length - 1) break;
        ranges[rangeIndex].startTime = ranges[rangeIndex - 1].endTime;
      }
    }

    setFieldValue("priceDetails", filteredDays.concat(ranges));
  };

  const priceGroupRangeSnippet = (detail) => {
    const timezoneOffsetStr = giveTimeAndUTCTimeOffset(
      new Date(new Date().toISOString())
    );
    return (
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.200",
          backgroundColor: "grey.50",
        }}
      >
        {values.priceGroupType === enums.PRICE_TYPE.VARIABLE && (
          <>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                >
                  <Icon icon="mdi:clock-outline" style={{ marginRight: 8 }} />
                  Time Range
                </Typography>
                <Stack direction="row" spacing={1}>
                  <FormControl fullWidth>
                    <Field name={`priceDetails[${detail.index}].startTime`}>
                      {({ field }) => (
                        <TextField
                          label="Start Time"
                          size="small"
                          type="time"
                          {...field}
                          value={field.value || "00:00"}
                          onBlur={() => onHandleBlur(field)}
                          InputLabelProps={{ shrink: !0 }}
                          inputProps={{ min: "00:00", max: "23:59" }}
                          error={Boolean(
                            touched.priceDetails &&
                              errors.priceDetails &&
                              errors?.priceDetails[detail.index]?.startTime
                          )}
                          helperText={
                            touched.priceDetails &&
                            errors.priceDetails &&
                            errors?.priceDetails[detail.index]?.startTime
                          }
                        />
                      )}
                    </Field>
                  </FormControl>
                  <FormControl fullWidth>
                    <Field name={`priceDetails[${detail.index}].endTime`}>
                      {({ field }) => (
                        <TextField
                          label="End Time"
                          size="small"
                          type="time"
                          {...field}
                          value={field.value || "23:59"}
                          onBlur={() => onHandleBlur(field)}
                          InputLabelProps={{ shrink: !0 }}
                          inputProps={{ min: "00:00", max: "23:59" }}
                          error={Boolean(
                            touched.priceDetails &&
                              errors.priceDetails &&
                              errors?.priceDetails[detail.index]?.endTime
                          )}
                          helperText={
                            touched.priceDetails &&
                            errors.priceDetails &&
                            errors?.priceDetails[detail.index]?.endTime
                          }
                        />
                      )}
                    </Field>
                  </FormControl>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  <Icon icon="mdi:information" style={{ marginRight: 4 }} />
                  Note: Timings should be in accordance with UTC timezone.{" "}
                  {timezoneOffsetStr}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ height: "100%", alignItems: "flex-end" }}
                >
                  {priceRangeInGroup[detail.day].length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={onAddTimeRange(detail.index, "R")}
                      startIcon={<Icon icon="mdi:delete" />}
                      size="small"
                    >
                      Remove
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={onAddTimeRange(detail.index, "S")}
                    startIcon={<Icon icon="mdi:refresh" />}
                    size="small"
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}

        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
        >
          <Icon icon="mdi:currency-usd" style={{ marginRight: 8 }} />
          Pricing Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Price Type
            </Typography>
            <FormControl fullWidth>
              <Field name={`priceDetails[${detail.index}].priceType`}>
                {({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    aria-labelledby="row-radio-buttons-price-type"
                    defaultValue={enums.PRICE_PER.KWH}
                    onChange={(e) => {
                      field.onChange(e);
                      // Clear the other price field when switching
                      if (e.target.value === enums.PRICE_PER.KWH) {
                        setFieldValue(
                          `priceDetails[${detail.index}].priceperHr`,
                          ""
                        );
                        setFieldValue(
                          `priceDetails[${detail.index}].priceperMin`,
                          ""
                        );
                      } else {
                        setFieldValue(
                          `priceDetails[${detail.index}].priceperKW`,
                          ""
                        );
                      }
                    }}
                    sx={{ gap: 1 }}
                  >
                    <Paper
                      elevation={
                        values.priceDetails[detail.index].priceType ===
                        enums.PRICE_PER.KWH
                          ? 2
                          : 0
                      }
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border:
                          values.priceDetails[detail.index].priceType ===
                          enums.PRICE_PER.KWH
                            ? 2
                            : 1,
                        borderColor:
                          values.priceDetails[detail.index].priceType ===
                          enums.PRICE_PER.KWH
                            ? "primary.main"
                            : "grey.300",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          elevation: 1,
                          borderColor: "primary.light",
                        },
                      }}
                      onClick={() =>
                        setFieldValue(
                          `priceDetails[${detail.index}].priceType`,
                          enums.PRICE_PER.KWH
                        )
                      }
                    >
                      <FormControlLabel
                        value={enums.PRICE_PER.KWH}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon
                              icon="mdi:lightning-bolt"
                              style={{ color: "#666" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Per KWh
                            </Typography>
                          </Box>
                        }
                        control={<Radio />}
                        sx={{ m: 0 }}
                      />
                    </Paper>
                    <Paper
                      elevation={
                        values.priceDetails[detail.index].priceType ===
                        enums.PRICE_PER.MIN
                          ? 2
                          : 0
                      }
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border:
                          values.priceDetails[detail.index].priceType ===
                          enums.PRICE_PER.MIN
                            ? 2
                            : 1,
                        borderColor:
                          values.priceDetails[detail.index].priceType ===
                          enums.PRICE_PER.MIN
                            ? "primary.main"
                            : "grey.300",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          elevation: 1,
                          borderColor: "primary.light",
                        },
                      }}
                      onClick={() =>
                        setFieldValue(
                          `priceDetails[${detail.index}].priceType`,
                          enums.PRICE_PER.MIN
                        )
                      }
                    >
                      <FormControlLabel
                        value={enums.PRICE_PER.MIN}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon icon="mdi:clock" style={{ color: "#666" }} />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Per Hour
                            </Typography>
                          </Box>
                        }
                        control={<Radio />}
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </RadioGroup>
                )}
              </Field>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            {values.priceDetails[detail.index].priceType ===
            enums.PRICE_PER.KWH ? (
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Price Per KWh
                </Typography>
                <Field name={`priceDetails[${detail.index}].priceperKW`}>
                  {({ field }) => (
                    <TextField
                      size="small"
                      {...field}
                      value={field.value || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:currency-inr"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                      error={Boolean(
                        touched.priceDetails &&
                          errors.priceDetails &&
                          errors?.priceDetails[detail.index]?.priceperKW
                      )}
                      helperText={
                        touched.priceDetails &&
                        errors.priceDetails &&
                        errors?.priceDetails[detail.index]?.priceperKW
                      }
                    />
                  )}
                </Field>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Price Per Hour
                </Typography>
                <Field name={`priceDetails[${detail.index}].priceperHr`}>
                  {({ field, form }) => (
                    <TextField
                      size="small"
                      {...field}
                      value={field.value || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:currency-inr"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                      error={Boolean(
                        touched.priceDetails &&
                          errors.priceDetails &&
                          errors?.priceDetails[detail.index]?.priceperHr
                      )}
                      helperText={
                        touched.priceDetails &&
                        errors.priceDetails &&
                        errors?.priceDetails[detail.index]?.priceperHr
                      }
                    />
                  )}
                </Field>
              </FormControl>
            )}
          </Grid>
          {/* <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            size="small"
            error={Boolean(touched.currency && errors.currency)}
          >
            <InputLabel id="currency">Currency</InputLabel>
            <Field name={`priceDetails[${detail.index}].currency`}>
              {({ field, form: { touched, errors } }) => (
                <>
                  <Select
                    labelId="currency"
                    id="currency_id"
                    disabled={isReadOnly}
                    label="Currency"
                    defaultValue={detail.currency}
                    {...field}
                  >
                    <MenuItem value="INR">Indian Rupee (INR)</MenuItem>
                    <MenuItem value="CAD">Canadian Dollar (CAD)</MenuItem>
                  </Select>
                  {touched.currency && (
                    <FormHelperText>{errors.currency}</FormHelperText>
                  )}
                </>
              )}
            </Field>
          </FormControl>
        </Grid> */}
          <Grid item xs={12} md={4} sx={{ display: "none" }}>
            <FormControl fullWidth>
              <Field name={`priceDetails[${detail.index}].fixedFeeStarting`}>
                {({ field, form: { touched, errors } }) => (
                  <TextField
                    label="Fixed Fee Starting"
                    size="medium"
                    disabled={isReadOnly}
                    {...field}
                    value={field.value || ""}
                    InputProps={{
                      startAdornment: (
                        <Icon
                          icon="mdi:play-circle"
                          style={{ marginRight: 8, color: "#666" }}
                        />
                      ),
                    }}
                    error={Boolean(
                      touched.priceDetails &&
                        errors.priceDetails &&
                        errors?.priceDetails[detail.index]?.fixedFeeStarting
                    )}
                    helperText={
                      touched.priceDetails &&
                      errors.priceDetails &&
                      errors?.priceDetails[detail.index]?.fixedFeeStarting
                    }
                  />
                )}
              </Field>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: "none" }}>
            <FormControl fullWidth>
              <Field name={`priceDetails[${detail.index}].fixedFeeCharging`}>
                {({ field, form: { touched, errors } }) => (
                  <TextField
                    label="Fixed Fee Charging"
                    size="medium"
                    disabled={isReadOnly}
                    {...field}
                    value={field.value || ""}
                    InputProps={{
                      startAdornment: (
                        <Icon
                          icon="mdi:flash"
                          style={{ marginRight: 8, color: "#666" }}
                        />
                      ),
                    }}
                    error={Boolean(
                      touched.priceDetails &&
                        errors.priceDetails &&
                        errors?.priceDetails[detail.index]?.fixedFeeCharging
                    )}
                    helperText={
                      touched.priceDetails &&
                      errors.priceDetails &&
                      errors?.priceDetails[detail.index]?.fixedFeeCharging
                    }
                  />
                )}
              </Field>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const priceRangeInGroup = useMemo(() => {
    return values.priceDetails.reduce((prev, curr, i) => {
      prev[curr.day] = prev[curr.day] || [];
      prev[curr.day].push({ ...curr, index: i });
      return prev;
    }, {});
  }, [values]);

  useEffect(() => {
    if (data) {
      // Sanitize data to prevent null/undefined values that cause controlled/uncontrolled warnings
      const sanitizedData = {
        ...data,
        priceDetails: data.priceDetails?.map(detail => ({
          ...detail,
          priceperMin: detail.priceperMin || "",
          priceperHr: detail.priceperHr || "",
          priceperKW: detail.priceperKW || "",
          fixedFeeStarting: detail.fixedFeeStarting || "",
          fixedFeeCharging: detail.fixedFeeCharging || "",
          startTime: detail.startTime || "00:00",
          endTime: detail.endTime || "23:59",
        })) || []
      };
      resetForm({ values: sanitizedData });
    }
  }, [data, resetForm]);

  useEffect(() => {
    const { priceType } = values;
    const field =
      priceType === enums.PRICE_PER.KWH ? "priceperMin" : "priceperKW";
    setFieldValue(field, "");
  }, [values?.priceType]);

  useEffect(() => {
    const { priceGroupType } = values;
    const range = is.empty(data)
      ? defaultValues.priceDetails
      : data.priceDetails;

    if (priceGroupType === enums.PRICE_TYPE.FIXED) {
      const _fixedRange = range.length > 1 ? defaultValues.priceDetails : range;
      const fixedPriceDetails = { ..._fixedRange[0] };
      fixedPriceDetails.day = 0;
      setFieldValue("priceDetails", [fixedPriceDetails]);
    }

    if (priceGroupType === enums.PRICE_TYPE.VARIABLE) {
      selectedRef.current = enums.DAYS["MONDAY"];
      setFieldValue(
        "priceDetails",
        range.length > 1 ? range : defaultValues.priceDetails
      );
      setPriceGroupState((state) => ({
        ...state,
        MONDAY: !0,
      }));
    }
  }, [values?.priceGroupType, data]);

  useEffect(() => {
    if (
      isCreatePriceGroup &&
      !isPaymentDetailsFetching &&
      !is.empty(paymentDetails)
    ) {
      const {
        paymentGatewayId,
        currency,
        paymentGateway,
        taxPercent,
        transactionPercent,
        pgChargesBearer,
      } = paymentDetails[0];
      const updatedValues = {
        ...values,
        paymentGatewayId: paymentGatewayId || "",
        currency: currency || "",
        paymentGateway: paymentGateway || "",
        taxPercent: taxPercent || "",
        transactionPercent: transactionPercent || "",
        pgChargesBearer: pgChargesBearer || "",
      };
      resetForm({ values: updatedValues });
      // setFieldValue("currency", currency);
      // setFieldValue("paymentGatewayId", paymentGatewayId);
      // setFieldValue("paymentGateway", paymentGateway);
      // setFieldValue("transactionPercent", transactionPercent);
      // setFieldValue("taxPercent", taxPercent);
    }
  }, [isPaymentDetailsFetching, resetForm]);

  // Clear submission error when user starts editing
  useEffect(() => {
    if (submissionError) {
      setSubmissionError(null);
    }
  }, [values]);


  if (isFetching) {
    return <Loader />;
  }

  if (!isCreatePriceGroup && is.empty(data)) {
    navigate("/pricegroup");
    return null;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          id="pricegroup"
          noValidate
          onSubmit={handleSubmit}
        >
          {/* Display submission errors */}
          {submissionError && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Submission Error:
                </Typography>
                <Typography variant="body2">
                  {submissionError}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Display validation errors */}
          {shouldShowFormErrors() && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Please fix the following errors:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {renderErrorMessages(errors).length > 0 ? (
                    renderErrorMessages(errors)
                  ) : (
                    <li>
                      <Typography variant="body2">
                        Please check all required fields and try again.
                      </Typography>
                    </li>
                  )}
                </ul>
              </Alert>
            </Box>
          )}
          
          {/* Basic Information Section */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Basic Information
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      required
                      label="Price Group Name"
                      size="medium"
                      {...getFieldProps("name")}
                      value={values.name || ""}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:tag-outline"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Description"
                      multiline
                      rows={2}
                      size="medium"
                      {...getFieldProps("description")}
                      value={values.description || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:text"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payment Configuration Section */}
          <Card sx={{ mb: 2, boxShadow: 1 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Payment Configuration
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, py: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <TextField
                      label="Currency"
                      disabled
                      size="medium"
                      {...getFieldProps("currency")}
                      value={values.currency || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:currency-inr"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <TextField
                      label="Payment Gateway"
                      disabled
                      size="medium"
                      {...getFieldProps("paymentGateway")}
                      value={values.paymentGateway || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:credit-card"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <TextField
                      label="Tax %"
                      disabled
                      size="medium"
                      {...getFieldProps("taxPercent")}
                      value={values.taxPercent || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:percent"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <TextField
                      label="Transaction %"
                      disabled
                      size="medium"
                      {...getFieldProps("transactionPercent")}
                      value={values.transactionPercent || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:percent"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <TextField
                      label="Charges Bearer"
                      disabled
                      size="medium"
                      {...getFieldProps("pgChargesBearer")}
                      value={values.pgChargesBearer || ""}
                      InputProps={{
                        startAdornment: (
                          <Icon
                            icon="mdi:account-arrow-right"
                            style={{ marginRight: 8, color: "#666" }}
                          />
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Price Type Selection */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Pricing Configuration
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        color: "text.primary",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Icon icon="mdi:cog" style={{ color: "#666" }} />
                      Price Type
                    </FormLabel>
                    <RadioGroup
                      row
                      {...getFieldProps("priceGroupType")}
                      aria-labelledby="row-radio-buttons-price-type"
                      sx={{ gap: 3 }}
                    >
                      <Paper
                        elevation={
                          values.priceGroupType === enums.PRICE_TYPE.FIXED
                            ? 2
                            : 0
                        }
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border:
                            values.priceGroupType === enums.PRICE_TYPE.FIXED
                              ? 2
                              : 1,
                          borderColor:
                            values.priceGroupType === enums.PRICE_TYPE.FIXED
                              ? "primary.main"
                              : "grey.300",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            elevation: 1,
                            borderColor: "primary.light",
                          },
                        }}
                        onClick={() =>
                          setFieldValue(
                            "priceGroupType",
                            enums.PRICE_TYPE.FIXED
                          )
                        }
                      >
                        <FormControlLabel
                          value={enums.PRICE_TYPE.FIXED}
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Icon
                                icon="mdi:clock-outline"
                                style={{ color: "#666" }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                Fixed Pricing
                              </Typography>
                            </Box>
                          }
                          control={<Radio />}
                          sx={{ m: 0 }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 4, display: "block" }}
                        >
                          Set a single price for all time periods
                        </Typography>
                      </Paper>
                      {/* Variable Pricing option - commented out */}
                      {/* <Paper 
                        elevation={values.priceGroupType === enums.PRICE_TYPE.VARIABLE ? 2 : 0}
                        sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          border: values.priceGroupType === enums.PRICE_TYPE.VARIABLE ? 2 : 1,
                          borderColor: values.priceGroupType === enums.PRICE_TYPE.VARIABLE ? 'primary.main' : 'grey.300',
                          cursor: 'not-allowed',
                          transition: 'all 0.2s ease-in-out',
                          opacity: 0.6,
                          '&:hover': {
                            elevation: 0,
                            borderColor: 'grey.300'
                          }
                        }}
                        onClick={undefined}
                      >
                        <FormControlLabel
                          value={enums.PRICE_TYPE.VARIABLE}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon icon="mdi:chart-line" style={{ color: '#999' }} />
                              <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.disabled' }}>
                                Variable Pricing
                              </Typography>
                            </Box>
                          }
                          control={<Radio disabled={true} />}
                          sx={{ m: 0 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                          Set different prices for different days and time periods
                        </Typography>
                      </Paper> */}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Day Selection for Variable Pricing */}
          {values.priceGroupType === enums.PRICE_TYPE.VARIABLE && (
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Day Selection
                  </Typography>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Select the days for which you want to configure pricing
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {Object.keys(enums.DAYS).map((day) => (
                        <Chip
                          key={day}
                          data-day={day}
                          className={`${priceGroupState[day] && "checked"}`}
                          onClick={handleEvent}
                          label={day}
                          variant={priceGroupState[day] ? "filled" : "outlined"}
                          color={priceGroupState[day] ? "primary" : "default"}
                          sx={{
                            minWidth: 80,
                            height: 36,
                            fontWeight: 500,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              boxShadow: 2,
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        onClick={onHandleCopy(
                          enums.DAYS_IN_STRING[selectedRef.current]
                        )}
                        startIcon={<Icon icon="ic:baseline-content-copy" />}
                        sx={{ minWidth: 100 }}
                      >
                        Copy
                      </Button>
                      <Button
                        variant="contained"
                        onClick={onAddTimeRange()}
                        startIcon={<Icon icon="ic:baseline-plus" />}
                        sx={{ minWidth: 100 }}
                      >
                        Add Time Range
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
          {/* Price Details Section */}
          <Card sx={{ mb: 2, boxShadow: 1 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Price Details
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, py: 1 }}>
              {values.priceGroupType === enums.PRICE_TYPE.FIXED && (
                <Grid container>
                  <Grid item xs={12}>
                    {priceRangeInGroup[0] &&
                      priceRangeInGroup[0].map((detail) => (
                        <div
                          key={
                            detail.index ||
                            detail.priceDetailsId ||
                            Math.random()
                          }
                        >
                          {priceGroupRangeSnippet(detail)}
                        </div>
                      ))}
                  </Grid>
                </Grid>
              )}
              {values.priceGroupType === enums.PRICE_TYPE.VARIABLE && (
                <>
                  <Alert 
                    severity="info" 
                    icon={<Icon icon="mdi:information" />}
                    sx={{ mb: 3 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Important Note:
                    </Typography>
                    <Typography variant="body2">
                      Price used to calculate transaction cost is applied based on the transaction start time.
                    </Typography>
                  </Alert>
                  <Grid item xs={12}>
                  {priceRangeInGroup[1] &&
                    priceGroupState[enums.DAYS_IN_STRING[1]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[1]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[1].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}

                  {priceRangeInGroup[2] &&
                    priceGroupState[enums.DAYS_IN_STRING[2]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[2]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[2].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}

                  {priceRangeInGroup[3] &&
                    priceGroupState[enums.DAYS_IN_STRING[3]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[3]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[3].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}

                  {priceRangeInGroup[4] &&
                    priceGroupState[enums.DAYS_IN_STRING[4]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[4]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[4].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}

                  {priceRangeInGroup[5] &&
                    priceGroupState[enums.DAYS_IN_STRING[5]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[5]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[5].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}

                  {priceRangeInGroup[6] &&
                    priceGroupState[enums.DAYS_IN_STRING[6]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[6]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[6].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}

                  {priceRangeInGroup[0] &&
                    priceGroupState[enums.DAYS_IN_STRING[0]] && (
                      <fieldset className="day-fieldset">
                        <legend>
                          {" "}
                          <Typography variant="h6">
                            {enums.DAYS_IN_STRING[0]}
                          </Typography>{" "}
                        </legend>
                        {priceRangeInGroup[0].map((detail) => (
                          <div
                            key={
                              detail.index ||
                              detail.priceDetailsId ||
                              Math.random()
                            }
                          >
                            {priceGroupRangeSnippet(detail)}
                          </div>
                        ))}
                      </fieldset>
                    )}
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
          {/* Action Buttons */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {!isReadOnly && (
                  <>
                    <Button
                      size="large"
                      type="button"
                      variant="outlined"
                      component={RouterLink}
                      to="/pricegroup"
                      startIcon={<Icon icon="mdi:close" />}
                      sx={{ minWidth: 120 }}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={isReadOnly}
                      loading={isSubmitting || isLoading}
                      startIcon={<Icon icon="mdi:content-save" />}
                      sx={{ minWidth: 120 }}
                    >
                      {isSubmitting || isLoading ? "Saving..." : "Save Changes"}
                    </LoadingButton>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Form>
      </FormikProvider>
      <PriceGroupCopyModal
        isAllSelected={priceGroupState.isAll}
        checkedChange={onCheckedChanges}
        copiedDays={priceGroupState.copy}
        onClose={() => dispatch(dialogSlice.actions.setDialog(false))}
        onCopy={onHandleCopy()}
      />
    </Box>
  );
};
